import React, { useEffect, useRef } from 'react';
import { Modal, Progress, Alert, Typography } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_BILL_RERUN_PROGRESS } from '../../graphql/queries/settlement/billRerunQueries';
import { BillRerunProgress, BillRerunTaskStatus } from '../../graphql/types/settlement/settlementBillTypes';
import { message } from 'antd';
import { getBillTypeLabel } from '../../utils/billI18n';

const { Text } = Typography;

interface Props {
  visible: boolean;
  taskId: string | null;
  onClose: () => void;
  onCompleted: (success: boolean) => void;
}

/**
 * 账单重跑进度弹窗
 * 
 * 功能：
 * - 实时显示重跑任务进度（每2秒轮询）
 * - 成功时显示结果并自动关闭
 * - 失败时显示错误信息
 */
export const BillRerunProgressModal: React.FC<Props> = ({
  visible,
  taskId,
  onClose,
  onCompleted
}) => {
  // 使用 ref 记录是否已经处理过完成状态，防止重复显示消息
  const hasCompletedRef = useRef(false);

  // 轮询查询进度（每2秒）
  const { data, stopPolling } = useQuery<{
    getBillRerunProgress: BillRerunProgress;
  }>(GET_BILL_RERUN_PROGRESS, {
    variables: { taskId },
    skip: !taskId || !visible,
    pollInterval: 2000,
    fetchPolicy: 'network-only'
  });

  // 当弹窗重新打开时，重置完成标志
  useEffect(() => {
    if (visible) {
      hasCompletedRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    if (data?.getBillRerunProgress && !hasCompletedRef.current) {
      const progress = data.getBillRerunProgress;
      
      if (progress.status === BillRerunTaskStatus.SUCCESS) {
        // 重跑成功
        hasCompletedRef.current = true;
        stopPolling();
        
        message.success('账单重跑成功！', 5);
        onCompleted(true);
        setTimeout(() => {
          onClose();
        }, 500);
      } else if (progress.status === BillRerunTaskStatus.FAILED) {
        // 重跑失败
        hasCompletedRef.current = true;
        stopPolling();
        message.error(
          `账单重跑失败: ${progress.errorMessage || '未知错误'}`,
          5
        );
        onCompleted(false);
      }
    }
  }, [data, stopPolling, onCompleted, onClose]);

  // 清理：当弹窗关闭时停止轮询
  useEffect(() => {
    if (!visible) {
      stopPolling();
    }
  }, [visible, stopPolling]);

  const progress = data?.getBillRerunProgress;

  // 获取进度条状态
  const getProgressStatus = () => {
    if (!progress) return 'active';
    if (progress.status === BillRerunTaskStatus.SUCCESS) return 'success';
    if (progress.status === BillRerunTaskStatus.FAILED) return 'exception';
    return 'active';
  };

  // 获取进度条颜色
  const getStrokeColor = () => {
    if (progress?.status === BillRerunTaskStatus.SUCCESS) {
      return '#52c41a';
    }
    return undefined;
  };

  // 允许用户随时关闭弹窗（任务在后台继续执行）
  const handleClose = () => {
    if (progress?.status === BillRerunTaskStatus.RUNNING) {
      message.info('任务将在后台继续执行，完成后会自动刷新列表');
    }
    onClose();
  };

  return (
    <Modal
      title="账单重跑进度"
      open={visible}
      onCancel={handleClose}
      footer={null}
      closable={true}
      maskClosable={false}
      width={500}
    >
      <div style={{ padding: '20px 0' }}>
        {progress && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>客户：</Text> {progress.clientName || '-'}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>账单类型：</Text> 
              {getBillTypeLabel(progress.billType)}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>账期实例ID：</Text> {progress.periodInstanceId}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>账期时间：</Text> 
              {progress.periodStart ? new Date(progress.periodStart).toLocaleString('zh-CN') : '-'} 
              {' 至 '}
              {progress.periodEnd ? new Date(progress.periodEnd).toLocaleString('zh-CN') : '-'}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>重跑原因：</Text> {progress.reason || '-'}
            </div>
          </div>
        )}
        
        <Progress 
          percent={progress?.progress || 0} 
          status={getProgressStatus()}
          strokeColor={getStrokeColor()}
        />
        
        <div style={{ 
          marginTop: 16, 
          textAlign: 'center', 
          minHeight: 24
        }}>
          <Text type="secondary">
            {progress?.currentStep || '任务已创建，等待处理...'}
          </Text>
        </div>
        
        {progress?.errorMessage && (
          <Alert
            message="错误信息"
            description={progress.errorMessage}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        
        {(!progress || progress.status === BillRerunTaskStatus.PENDING) && (
          <Alert
            message="任务已创建"
            description="系统将在 30 秒内开始处理此任务，请耐心等待..."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        
        {progress?.status === BillRerunTaskStatus.RUNNING && (
          <div style={{ 
            marginTop: 16, 
            textAlign: 'center',
            fontSize: 12,
            color: '#999'
          }}>
            正在处理中，请稍候...
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BillRerunProgressModal;

